<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { useSettingStore } from './stores/settingStore'
import { useUiStore } from './stores/uiStore'
import OtaUpdate from './components/common/OtaUpdate.vue'
import { notifyAppReady } from './utils/otaUpdate'
import { initBaguQuestions } from './utils/baguInitializer'
import { requestPersistentStorage } from './utils/storagePersist'

const settingStore = useSettingStore()
const uiStore = useUiStore()
const router = useRouter()

onMounted(async () => {
  // 最先调用: 告诉 Capgo 插件 WebView 已启动,防止超时回滚(不等 DB 初始化)
  notifyAppReady()
  await settingStore.loadSettings()

  // 首次启动时导入八股文题库到知识库(幂等,不阻塞 UI)
  initBaguQuestions()

  // 首次用户交互时请求浏览器存储持久化（防止系统自动清除 IndexedDB）
  // 必须在用户手势上下文中调用，所以监听 click/touchstart
  let storageRequested = false
  function onFirstInteraction() {
    if (storageRequested) return
    storageRequested = true
    requestPersistentStorage()
    document.removeEventListener('click', onFirstInteraction)
    document.removeEventListener('touchstart', onFirstInteraction)
  }
  document.addEventListener('click', onFirstInteraction, { once: false })
  document.addEventListener('touchstart', onFirstInteraction, { once: false })

  // Android 硬件返回键处理
  if (Capacitor.isNativePlatform()) {
    const { App } = await import('@capacitor/app')
    App.addListener('backButton', () => {
      // 1. 先关闭所有浮层/弹窗
      if (uiStore.showTaskForm) { uiStore.closeTaskForm(); return }
      if (uiStore.showTaskCard) { uiStore.closeTaskCard(); return }
      if (uiStore.showSearchPanel) { uiStore.toggleSearchPanel(); return }
      if (uiStore.showStatsPanel) { uiStore.toggleStatsPanel(); return }
      if (uiStore.showBulkDialog) { uiStore.showBulkDialog = false; return }
      if (uiStore.showProfileDialog) { uiStore.showProfileDialog = false; return }
      if (uiStore.showMealLog) { uiStore.showMealLog = false; return }
      if (uiStore.showAiChat) { uiStore.closeAiChat(); return }

      // 2. 不在首页 → 返回上一页
      if (router.currentRoute.value.path !== '/') {
        router.back()
        return
      }

      // 3. 在首页 → 退出应用
      App.exitApp()
    })
  }
})
</script>

<template>
  <div class="app" :class="{ dark: settingStore.isDark() }">
    <RouterView />
    <OtaUpdate />
  </div>
</template>

<style lang="scss">
@use './styles/index.scss' as *;
@use './styles/calendar.scss' as *;
@use './styles/ios-theme.scss' as *;

.app {
  min-height: 100vh;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: background 0.3s ease;
}
</style>
